import { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Tr } from '@utils';
import { PORTAL_URL } from '@models';
import { useService, useLoggedinUser } from '@hooks';
import { Organization, Permission, PredefinedRole, Role, userService } from '@services';
import { setAssumedOrganization, setAssumedRole } from '@store/slices/portal.slice';
import { Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

type EntityType = Organization;

export interface IClientAccountListProps extends IEntityListProps<EntityType> {
	paddingX?: number | string;
	entities: EntityType[];
	selected: EntityType[];
	onSelectChange?: (selected: EntityType[]) => void;
	onView?: (entity: EntityType) => void;
	onEdit?: (entity: EntityType) => void;
	onDelete?: (entity: EntityType) => void;
	onAssignRole?: (entity: EntityType) => void;
}

export const ClientAccountList: FC<IClientAccountListProps> = ({
	entities,
	selected,
	onSelectChange,
	onView,
	onEdit,
	onDelete,
	onAssignRole,
	paddingX,
	...props
}) => {
	const dispatch = useDispatch();
	const user = useLoggedinUser();
	const { data: organizations = [] } = useService(() => userService.doIfNotAdmin(userService.getSelectableRoles()), [entities]);
	const hasWritePermission = useMemo(() => user?.hasPermission(Permission.CREATE_ORG), [user]);
	const hasDeletePermission = useMemo(() => user?.hasPermission(Permission.DELETE_ORG), [user]);
	const assumableRoleName = useMemo(
		() => (user?.isSeller() ? (user.isChargingPartner() ? PredefinedRole.FACILITY_MANAGER : PredefinedRole.FLEET_MANAGER) : undefined),
		[user]
	);
	const handleSelectChange = (status: boolean, entity: EntityType) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};
	const getRole = useCallback(
		(org?: string) =>
			organizations.find(({ organization: { id } }) => id === org)?.roles?.find(({ name }) => name === assumableRoleName),
		[organizations, assumableRoleName]
	);
	const onSwitchRole = (org: Organization, role: Role): void => {
		dispatch(setAssumedOrganization(org));
		dispatch(setAssumedRole(role));
		setTimeout(() => (window.location.href = `/${PORTAL_URL}`), 500);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!hasWritePermission}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => (
				<EntityListItem
					key={`entity-${entity.id}-${i}`}
					sx={{ paddingRight: paddingX }}
					selected={!!selected.find((s) => s.id === entity.id)}
					actions={[
						{ text: <Tr.Portal path="view" />, onClick: () => onView?.(entity) },
						{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !hasWritePermission },
						{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !hasDeletePermission },
						{
							text: <Tr.Portal path="client-account.assign-role" />,
							onClick: () => onAssignRole?.(entity),
							hidden: !hasWritePermission
						}
					]}
					notSelectable={!hasWritePermission}
					onSelectChange={(status) => handleSelectChange(status, entity)}
				>
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: 1.0 }}>
						<Grid container sx={{ flexGrow: 1 }}>
							<Grid item xs={12}>
								<EntityListItemCell title={<Tr.Portal path="client-account.client-account" />}>
									<Typography>{entity.name}</Typography>
								</EntityListItemCell>
							</Grid>
						</Grid>
						<Tooltip title={<Tr.Portal path="client-account.switch-action-tooltip" />} placement="top" arrow>
							<IconButton
								onClick={() => {
									const role = getRole(entity.id);
									role && onSwitchRole(entity, role);
								}}
							>
								<ArrowOutwardIcon />
							</IconButton>
						</Tooltip>
					</Stack>
				</EntityListItem>
			))}
		</EntityList>
	);
};
